import Dexie from 'dexie';
import { AuthExpiredError, NetworkError, TimeoutError } from './fetch-utils';
import { humanizeFieldName, relabelRailsFullMessage } from './recorded_at';

/**
 * Thrown by the data layer's guards when an operation is attempted with no
 * server connectivity. A typed sentinel (rather than a message substring) so
 * classification below can't be fooled by an unrelated message that happens
 * to contain the word "offline".
 */
export class OfflineError extends Error {
  constructor(message = 'Cannot sync while offline') {
    super(message);
    this.name = 'OfflineError';
  }
}

/**
 * Whether a sync failure is a "not right now" problem (no connectivity, or the
 * encrypted database is closed because the app is locked) rather than
 * something wrong with the data or the server. Transient failures may be
 * retried immediately and are never surfaced to the user as a failed sync.
 */
export function isTransientSyncError(error: unknown): boolean {
  return (
    error instanceof OfflineError ||
    error instanceof NetworkError ||
    error instanceof TimeoutError ||
    error instanceof Dexie.DatabaseClosedError
  );
}

/**
 * Translate a sync failure into something a caseworker (or support) can act on.
 */
export function describeSyncError(error: unknown): string {
  if (error instanceof AuthExpiredError) {
    return 'Your Mon Ami session has expired. Sign in again to continue syncing. Your work is safe on this device.';
  }
  if (error instanceof TimeoutError) {
    return 'Mon Ami took too long to respond. Check your connection and try again in a moment.';
  }
  if (error instanceof NetworkError) {
    return 'Mon Ami could not be reached. Check your internet connection and try again.';
  }
  if (error instanceof OfflineError) {
    return 'This device is offline. Sync will run automatically as soon as you reconnect.';
  }
  if (error instanceof Dexie.DatabaseClosedError) {
    return 'The app was locked while syncing. Sync will run automatically now that it is unlocked.';
  }
  const message = error instanceof Error ? error.message : String(error);
  return `Sync failed: ${message}`;
}

/**
 * Turn a persisted `sync_error_message` into something a caseworker can act on.
 *
 * The update path builds the message as `"<attribute>: <server message>"`
 * (joined with "; " when several fields were rejected), so it names fields the
 * way ActiveRecord does — `recorded_at`, not "Date Completed". Nothing in the
 * UI calls a field by its attribute name, so rewrite each part into the label
 * the user saw when they typed the value. Anything that isn't in that shape
 * (a bare `errorData.message`, "Sync failed: 500") is passed through as-is.
 */
export function describeDocumentSyncError(message: string): string {
  return message
    .split('; ')
    .map((part) => {
      const match = /^([a-z][a-z0-9_]*): (.+)$/.exec(part);
      return match ? `${humanizeFieldName(match[1])} ${match[2]}` : relabelRailsFullMessage(part);
    })
    .join('; ');
}
